import { Brain, Microscope, ShieldCheck, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ScienceVisual from "./ScienceVisual";

const POINTS = [
  { icon: Brain, title: "Multi-Dimensional, Not One Label", text: "Instead of assigning a single simplistic personality type, CareerStudy360 examines multiple dimensions and studies how they interact." },
  { icon: Microscope, title: "Grounded in Established Research", text: "Built on established psychometric principles from personality psychology, vocational-interest research, cognitive aptitude and occupational science." },
  { icon: ShieldCheck, title: "Validation-Ready, Not Validation-Claimed", text: "Our own large-scale reliability, validity and fairness studies are underway — we're upfront about where that stands today." },
];

const FRAMEWORKS = [
  "Big Five (Five-Factor Model)", "Holland's RIASEC Interest Model", "Cognitive Aptitude Testing",
  "Situational Judgment Testing (SJT)", "Occupational Information Databases",
];

export default function ScienceSection() {
  const navigate = useNavigate();
  return (
    <section id="science" className="bg-white py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-indigo-600 mb-3 text-center">The Science</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 text-center max-w-3xl mx-auto mb-6">
          Career Guidance Grounded in Behavioural and Occupational Science
        </h2>
        <p className="text-base text-slate-600 text-center max-w-2xl mx-auto mb-16 leading-relaxed">
          CareerStudy360 is designed using research-grounded principles from personality psychology, vocational-interest
          research, cognitive aptitude, work values and occupational science — to help you identify patterns of
          alignment, not tell you what you "must become".
        </p>

        <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center mb-16">
          <div className="grid sm:grid-cols-3 gap-8">
            {POINTS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-slate-100 p-8 hover:border-indigo-200 transition-colors">
                <div className="h-12 w-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-5">
                  <p.icon className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-8 sm:p-12 grid sm:grid-cols-[auto_1fr] gap-10 items-center">
          <ScienceVisual />
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Grounded in Established Frameworks</h3>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              CareerStudy360's approach draws on decades of published research across behavioural and occupational
              science, rather than a single proprietary test. Specific scoring methodology remains confidential to
              protect assessment integrity.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {FRAMEWORKS.map((f) => (
                <span key={f} className="text-xs rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 px-3 py-1.5">{f}</span>
              ))}
            </div>
            <button
              type="button"
              data-testid="read-full-science-button"
              onClick={() => navigate("/science")}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
            >
              Read the Full Science <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
